import { BREAKPOINTS } from "configs/constants";
import { CfFileImage, ModCfFile, ModCfText, ModLang } from "./cf-utils/types";
import {
  Infer,
  array,
  assign,
  boolean,
  enums,
  nullable,
  object,
  string,
} from "superstruct";
import { InspectorModeTags } from "@contentful/live-preview/dist/inspectorMode/types";
import { LoadingType, PriorityType } from "components/Image/types";
import { ModCfImageIcon } from "components/ImageIcon/types";
import { icons } from "components/Icon/icons";
import React, { CSSProperties } from "react";

export const ModCfIconNames = enums([...Object.keys(icons)]);

export type Langs = Infer<typeof ModLang>;

export const ModCfIconText = object({
  id: string(),
  imageIcon: array(ModCfImageIcon),
  text: ModCfText,
});

export const ModCfCustomEvent = object({ id: string(), value: string() });

export const ModLabelCtaProps = object({
  id: string(),
  slug: string(),
  text: object({ text: string() }),
});

export const ModTranslation = object({
  id: string(),
  slug: string(),
  text: object({ text: string() }),
});

export const ModCfTranslation = object({
  id: string(),
  slug: string(),
  text: ModCfText,
});

export type CfTranslation = Infer<typeof ModCfTranslation>;

export const ModCfTranslations = object({
  id: string(),
  translations: array(ModCfTranslation),
});

export type CfTranslations = Infer<typeof ModCfTranslations>;

export const ModCfMetaRobots = enums([
  "all",
  "none",
  "noindex,follow",
  "nofollow",
]);

type CfMetaRobots = Infer<typeof ModCfMetaRobots>;

export interface PageHrefLang {
  en: string;
  it: string;
}

export const ModCfSettingsShared = object({
  publicPage: nullable(boolean()),
  metaRobots: ModCfMetaRobots,
  footer: object({
    id: string(),
    __typename: string(),
    contentful_id: string(),
    configuration: object({
      internal: object({
        content: string(),
      }),
    }),
  }),
});

export type CfSettingsPage = Infer<typeof ModCfSettingsShared>;

export const ModCfSettingsPageExtra = assign(
  object({
    translations: nullable(array(ModCfTranslations)),
  }),
  ModCfSettingsShared,
);

export type CfSettingsPageExtra = Infer<typeof ModCfSettingsPageExtra>;

export interface HeadConfig {
  pageHrefLang: PageHrefLang;
  lang: Langs;
  robots: CfMetaRobots;
  seo: CfSeo;
  location: {
    href: string;
  };
  jsonLd: string | undefined;
}

export const ModCfSeo = object({
  contentful_id: string(),
  // TODO: eng fields missing - fill and remove nullable
  pageTitle: nullable(string()),
  // TODO: eng fields missing - fill and remove nullable
  pageDescription: nullable(string()),
  ogPageTitle: nullable(string()),
  ogPageDescription: nullable(string()),
  ogPageType: nullable(enums(["article", "website", "video.other"])),
  ogPageImage: ModCfFile,
});

type CfSeo = Infer<typeof ModCfSeo>;

export type IconTextPropsCf = Infer<typeof ModCfIconText>;

// NOTE: object with all generic transalction
export interface GetTranslationsObj {
  [key: string]: string;
}

export interface SimpleObject {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export const ModCfNewsEventsCategory = object({
  id: string(),
  contentful_id: string(),
  node_locale: string(),
  slug: string(),
  // TODO: eng fields missing - fill and remove nullable
  title: nullable(string()),
});

export type CfNewsEventsCategory = Infer<typeof ModCfNewsEventsCategory>;

export type CustomEvent = Infer<typeof ModCfCustomEvent>;

export interface PortalProps {
  children: React.ReactNode;
}

export type GenericImages = {
  imageDesktop: CfFileImage;
  imageMobile?: CfFileImage;
};

export type ConvertGenericImageProps = {
  alt: string | null;
  slug: string | null;
  customClass: string | null;
} & GenericImages &
  GenericImageSettings;

export type GenericImageSettings = {
  loadingType?: LoadingType;
  featchPriority?: PriorityType;
  objectFit?: CSSProperties["objectFit"];
  breakpoint?: BREAKPOINTS;
};

export type LiveInspectorData = Record<string, InspectorModeTags> | undefined;
